"use client";

import Link from "next/link";
import { Shield, Lightbulb, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

interface ValueProp {
  icon: React.ReactNode;
  titleKey: string;
  descKey: string;
}

export default function AboutPage() {
  const { t } = useI18n();

  const values: ValueProp[] = [
    {
      icon: <Eye size={32} className="text-keiro-green" />,
      titleKey: "pages.about.values.transparency.title",
      descKey: "pages.about.values.transparency.desc",
    },
    {
      icon: <Shield size={32} className="text-keiro-green" />,
      titleKey: "pages.about.values.security.title",
      descKey: "pages.about.values.security.desc",
    },
    {
      icon: <Lightbulb size={32} className="text-keiro-green" />,
      titleKey: "pages.about.values.innovation.title",
      descKey: "pages.about.values.innovation.desc",
    },
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {t("pages.about.headline") || "О Keiront"}
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {t("pages.about.subheading") ||
              "Помогаем отелям принимать финансовые решения в нужный момент"}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.about.mission.title") || "Наша миссия"}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              {t("pages.about.mission.content") ||
                `Отельный бизнес требует быстрых и точных финансовых решений.
                Но большинство отелей всё ещё управляют казначейством вручную,
                в Excel'е и SMS. Это медленно, неправильно, и опасно.`}
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              {t("pages.about.mission.vision") ||
                `Keiront — это финансовый интеллект в облаке.
                Мы автоматизируем казначейство, управленческий учёт и KPI,
                чтобы отельеры и гостелеры могли сосредоточиться на гостях.`}
            </p>
          </div>
          <div className="bg-slate-50 rounded-xl p-8 border border-slate-200">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide font-semibold">
                  {t("pages.about.stats.clients") || "Клиентов"}
                </p>
                <p className="text-4xl font-bold text-keiro-green mt-2">40+</p>
              </div>
              <div className="h-px bg-slate-200" />
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide font-semibold">
                  {t("pages.about.stats.properties") ||
                    "Отелей подключено"}
                </p>
                <p className="text-4xl font-bold text-keiro-green mt-2">150+</p>
              </div>
              <div className="h-px bg-slate-200" />
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide font-semibold">
                  {t("pages.about.stats.saved") || "Часов экономлено в год"}
                </p>
                <p className="text-4xl font-bold text-keiro-green mt-2">
                  5000+
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.about.values.title") || "Наши ценности"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {values.map((value, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-8">
              <div className="mb-4">{value.icon}</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                {t(value.titleKey) || value.titleKey}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t(value.descKey) || value.descKey}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Story Section */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12">
          {t("pages.about.story.title") || "Почему мы это создали"}
        </h2>
        <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
          <p>
            {t("pages.about.story.p1") ||
              `Всё началось с простого наблюдения. Когда один из основателей
              консультировал сеть отелей из 12 объектов, он обнаружил, что
              казначей тратит 15 часов в неделю на ручное согласование платежей.`}
          </p>
          <p>
            {t("pages.about.story.p2") ||
              `Платежи одобрялись день-два, данные не синхронизировались с 1С,
              никто не знал, когда приходит зарплата. Это портило бизнес.`}
          </p>
          <p>
            {t("pages.about.story.p3") ||
              `Мы решили создать систему, которая автоматизирует весь цикл:
              от заявки на оплату до утверждения, прогноза кассовых потоков и
              интеграции с учётом. Вот так рождалась Keiront.`}
          </p>
          <p>
            {t("pages.about.story.p4") ||
              `Сегодня платформу используют крупные сети (100-500 номеров)
              и небольшие бутик-отели. Всех объединяет одно: они экономят
              десятки часов в месяц и принимают лучшие финансовые решения.`}
          </p>
        </div>
      </section>

      {/* Team Placeholder */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-8">
          {t("pages.about.team.title") || "Команда"}
        </h2>
        <p className="text-lg text-slate-600 mb-8">
          {t("pages.about.team.desc") ||
            `Мы — команда финансистов, разработчиков и специалистов
            по гостеприимству. Каждый день мы работаем с отелями разного размера
            и понимаем их боли.`}
        </p>
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-12 text-center">
          <p className="text-slate-600">
            {t("pages.about.team.coming") ||
              "Фотографии и истории команды скоро появятся"}
          </p>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.about.cta.title") || "Готовы попробовать?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.about.cta.desc") ||
              "Запишитесь на 30-минутную демонстрацию платформы"}
          </p>
          <Button asChild variant="default" className="h-12 px-8">
            <Link href="/contact">
              {t("pages.about.cta.button") || "Запросить демо"}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
